/**
 * LoggerMetrics - Worker 로거 성능 측정 클래스
 *
 * 측정 항목:
 * - Main Thread blocking 시간 (log() 호출 → 반환)
 * - Long Task 발생 빈도 (50ms 이상 blocking)
 * - Worker 전송 횟수/성공률
 *
 * debug: true일 때만 활성화됩니다.
 */

import type { Attributes } from './types';

export interface LongTaskEntry {
  startTime: number;
  duration: number;
}

export interface MainThreadReport {
  // Long Task 통계
  longTaskCount: number;
  totalLongTaskTime: number;
  avgLongTaskTime: number;
  maxLongTaskTime: number;

  // 로깅으로 인한 blocking
  totalBlockingTime: number;
  avgBlockingTime: number;

  // 감축률 (비교용)
  longTasks: LongTaskEntry[];
}

export interface MetricsSummary {
  // 호출 통계
  totalCalls: number;
  totalWorkerCalls: number;
  totalSdkCalls: number;

  // 시간 통계 (ms)
  avgBlockingTime: number;
  maxBlockingTime: number;
  minBlockingTime: number;
  p95BlockingTime: number;

  // 시간 배열 (상세 분석용)
  blockingTimes: number[];
}

export class LoggerMetrics {
  private enabled = false;
  private blockingTimes: number[] = [];
  private workerCalls = 0;
  private sdkCalls = 0;

  // Long Task 측정
  private longTasks: LongTaskEntry[] = [];
  private longTaskObserver: PerformanceObserver | null = null;

  /**
   * 메트릭 수집 활성화
   */
  enable(): void {
    this.enabled = true;
    this.reset();
    this.startLongTaskObserver();
    console.log('[LoggerMetrics] Enabled (Long Task monitoring started)');
  }

  /**
   * 메트릭 수집 비활성화
   */
  disable(): void {
    this.enabled = false;
    this.stopLongTaskObserver();
    console.log('[LoggerMetrics] Disabled');
  }

  /**
   * 메트릭 초기화
   */
  reset(): void {
    this.blockingTimes = [];
    this.workerCalls = 0;
    this.sdkCalls = 0;
    this.longTasks = [];
  }

  /**
   * Long Task Observer 시작 (50ms 이상 blocking 감지)
   */
  private startLongTaskObserver(): void {
    if (typeof PerformanceObserver === 'undefined') return;

    try {
      this.longTaskObserver = new PerformanceObserver(list => {
        for (const entry of list.getEntries()) {
          this.longTasks.push({
            startTime: entry.startTime,
            duration: entry.duration,
          });
        }
      });
      this.longTaskObserver.observe({ entryTypes: ['longtask'] });
    } catch {
      console.warn('[LoggerMetrics] Long Task API not supported');
    }
  }

  /**
   * Long Task Observer 중지
   */
  private stopLongTaskObserver(): void {
    if (this.longTaskObserver) {
      this.longTaskObserver.disconnect();
      this.longTaskObserver = null;
    }
  }

  /**
   * log() 호출 시간 측정 시작
   */
  startLogCall(): number {
    if (!this.enabled) return 0;
    return performance.now();
  }

  /**
   * log() 호출 시간 측정 종료
   */
  endLogCall(startTime: number, usedWorker: boolean): void {
    if (!this.enabled || startTime === 0) return;

    const duration = performance.now() - startTime;
    this.blockingTimes.push(duration);

    if (usedWorker) {
      this.workerCalls++;
    } else {
      this.sdkCalls++;
    }
  }

  /**
   * 현재까지의 메트릭 요약
   */
  getSummary(): MetricsSummary {
    const times = [...this.blockingTimes];
    const sorted = times.sort((a, b) => a - b);

    return {
      totalCalls: this.workerCalls + this.sdkCalls,
      totalWorkerCalls: this.workerCalls,
      totalSdkCalls: this.sdkCalls,

      avgBlockingTime:
        times.length > 0 ? times.reduce((a, b) => a + b, 0) / times.length : 0,
      maxBlockingTime: times.length > 0 ? Math.max(...times) : 0,
      minBlockingTime: times.length > 0 ? Math.min(...times) : 0,
      p95BlockingTime:
        times.length > 0 ? sorted[Math.floor(times.length * 0.95)] || 0 : 0,

      blockingTimes: times,
    };
  }

  /**
   * 콘솔에 메트릭 출력
   */
  printSummary(): void {
    const summary = this.getSummary();

    console.group('[LoggerMetrics] Summary');
    console.log(`Total calls: ${summary.totalCalls}`);
    console.log(
      `  - Worker: ${summary.totalWorkerCalls} (${(
        (summary.totalWorkerCalls / summary.totalCalls) * 100 || 0
      ).toFixed(1)}%)`,
    );
    console.log(
      `  - SDK: ${summary.totalSdkCalls} (${(
        (summary.totalSdkCalls / summary.totalCalls) * 100 || 0
      ).toFixed(1)}%)`,
    );
    console.log('');
    console.log('Blocking Time (ms):');
    console.log(`  - Avg: ${summary.avgBlockingTime.toFixed(3)}`);
    console.log(`  - Min: ${summary.minBlockingTime.toFixed(3)}`);
    console.log(`  - Max: ${summary.maxBlockingTime.toFixed(3)}`);
    console.log(`  - P95: ${summary.p95BlockingTime.toFixed(3)}`);
    console.groupEnd();
  }

  /**
   * Main Thread 리포트 생성
   */
  getMainThreadReport(): MainThreadReport {
    const times = this.blockingTimes;
    const totalBlockingTime = times.reduce((a, b) => a + b, 0);

    const longTaskDurations = this.longTasks.map(t => t.duration);
    const totalLongTaskTime = longTaskDurations.reduce((a, b) => a + b, 0);

    return {
      longTaskCount: this.longTasks.length,
      totalLongTaskTime,
      avgLongTaskTime:
        longTaskDurations.length > 0
          ? totalLongTaskTime / longTaskDurations.length
          : 0,
      maxLongTaskTime:
        longTaskDurations.length > 0 ? Math.max(...longTaskDurations) : 0,

      totalBlockingTime,
      avgBlockingTime: times.length > 0 ? totalBlockingTime / times.length : 0,

      longTasks: [...this.longTasks],
    };
  }

  /**
   * Main Thread 리포트 출력
   */
  printMainThreadReport(): void {
    const report = this.getMainThreadReport();

    console.group('[LoggerMetrics] Main Thread Report');
    console.log('Long Tasks (50ms+ blocking):');
    console.log(`  - Count: ${report.longTaskCount}`);
    console.log(`  - Total Time: ${report.totalLongTaskTime.toFixed(1)} ms`);
    console.log(`  - Avg Time: ${report.avgLongTaskTime.toFixed(1)} ms`);
    console.log(`  - Max Time: ${report.maxLongTaskTime.toFixed(1)} ms`);
    console.log('');
    console.log('Logger Blocking:');
    console.log(`  - Total Time: ${report.totalBlockingTime.toFixed(3)} ms`);
    console.log(`  - Avg Time: ${report.avgBlockingTime.toFixed(3)} ms`);
    console.groupEnd();
  }

  /**
   * 간단한 벤치마크 실행
   */
  static async benchmark(
    logFn: (event: string, attrs?: Attributes) => void,
    iterations = 100,
  ): Promise<{
    avg: number;
    min: number;
    max: number;
    p95: number;
    times: number[];
  }> {
    const times: number[] = [];

    // Warm up
    for (let i = 0; i < 10; i++) {
      logFn('benchmark_warmup', { i });
    }

    await new Promise(r => setTimeout(r, 100));

    // Benchmark
    for (let i = 0; i < iterations; i++) {
      const start = performance.now();
      logFn('benchmark_event', { iteration: i, data: 'x'.repeat(100) });
      times.push(performance.now() - start);
    }

    const sorted = [...times].sort((a, b) => a - b);

    return {
      avg: times.reduce((a, b) => a + b) / times.length,
      min: Math.min(...times),
      max: Math.max(...times),
      p95: sorted[Math.floor(times.length * 0.95)],
      times,
    };
  }

  /**
   * Worker vs SDK 비교 벤치마크 (A/B 테스트)
   *
   * @param workerLogFn Worker 모드 로그 함수
   * @param sdkLogFn SDK 직접 모드 로그 함수
   * @param iterations 반복 횟수
   */
  static async compareWorkerVsSdk(
    workerLogFn: (event: string, attrs?: Attributes) => void,
    sdkLogFn: (event: string, attrs?: Attributes) => void,
    iterations = 100,
  ): Promise<{
    worker: { avg: number; p95: number; total: number };
    sdk: { avg: number; p95: number; total: number };
    reduction: { avgPercent: number; p95Percent: number; totalPercent: number };
  }> {
    console.log(
      `[LoggerMetrics] Starting A/B comparison (${iterations} iterations each)...`,
    );

    // Worker 모드 벤치마크
    console.log('[LoggerMetrics] Testing Worker mode...');
    const workerResult = await LoggerMetrics.benchmark(workerLogFn, iterations);

    await new Promise(r => setTimeout(r, 500));

    // SDK 직접 모드 벤치마크
    console.log('[LoggerMetrics] Testing SDK Direct mode...');
    const sdkResult = await LoggerMetrics.benchmark(sdkLogFn, iterations);

    const workerTotal = workerResult.times.reduce((a, b) => a + b, 0);
    const sdkTotal = sdkResult.times.reduce((a, b) => a + b, 0);

    const result = {
      worker: {
        avg: workerResult.avg,
        p95: workerResult.p95,
        total: workerTotal,
      },
      sdk: {
        avg: sdkResult.avg,
        p95: sdkResult.p95,
        total: sdkTotal,
      },
      reduction: {
        avgPercent: ((sdkResult.avg - workerResult.avg) / sdkResult.avg) * 100,
        p95Percent: ((sdkResult.p95 - workerResult.p95) / sdkResult.p95) * 100,
        totalPercent: ((sdkTotal - workerTotal) / sdkTotal) * 100,
      },
    };

    // 결과 출력
    console.group('[LoggerMetrics] A/B Comparison Results');
    console.log(`Iterations: ${iterations}`);
    console.log('');
    console.log('Worker Mode:');
    console.log(`  - Avg: ${result.worker.avg.toFixed(3)} ms`);
    console.log(`  - P95: ${result.worker.p95.toFixed(3)} ms`);
    console.log(`  - Total: ${result.worker.total.toFixed(3)} ms`);
    console.log('');
    console.log('SDK Direct Mode:');
    console.log(`  - Avg: ${result.sdk.avg.toFixed(3)} ms`);
    console.log(`  - P95: ${result.sdk.p95.toFixed(3)} ms`);
    console.log(`  - Total: ${result.sdk.total.toFixed(3)} ms`);
    console.log('');
    console.log('🎯 Main Thread Reduction:');
    console.log(`  - Avg: ${result.reduction.avgPercent.toFixed(1)}%`);
    console.log(`  - P95: ${result.reduction.p95Percent.toFixed(1)}%`);
    console.log(`  - Total: ${result.reduction.totalPercent.toFixed(1)}%`);
    console.groupEnd();

    return result;
  }
}

// 싱글톤 인스턴스
export const loggerMetrics = new LoggerMetrics();
